import { useState, useEffect } from 'react';
import { Plus, Trash2, AlertTriangle, ChevronDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { api } from '../../../core/apiClient';
import { useAuth } from '../../../auth/AuthContext';

interface SupplierIncident {
  id: string;
  title: string;
  description?: string;
  type: string;
  severity: string;
  status: string;
  detectedAt: string;
  resolvedAt?: string | null;
  resolution?: string | null;
  assignee?: string | null;
  purchaseOrderId?: string | null;
  createdAt?: string;
}

const incidentTypes: Record<string, string> = {
  QUALITAT: 'Qualitat',
  TERMINI_ENTREGA: 'Termini entrega',
  QUANTITAT: 'Quantitat',
  DOCUMENTACIO: 'Documentació',
  FACTURACIO: 'Facturació',
  ALTRES: 'Altres',
};

const severityMap: Record<string, string> = {
  BAIXA: 'Baixa',
  MITJANA: 'Mitjana',
  ALTA: 'Alta',
  CRITICA: 'Crítica',
};

const severityColors: Record<string, string> = {
  BAIXA: 'bg-blue-100 text-blue-800',
  MITJANA: 'bg-yellow-100 text-yellow-800',
  ALTA: 'bg-orange-100 text-orange-800',
  CRITICA: 'bg-red-100 text-red-800',
};

const statusMap: Record<string, string> = {
  OBERTA: 'Oberta',
  EN_PROCES: 'En procés',
  RESOLTA: 'Resolta',
  TANCADA: 'Tancada',
  CANCELLADA: 'Cancel·lada',
};

const statusColors: Record<string, string> = {
  OBERTA: 'bg-blue-100 text-blue-800',
  EN_PROCES: 'bg-amber-100 text-amber-800',
  RESOLTA: 'bg-green-100 text-green-800',
  TANCADA: 'bg-slate-100 text-slate-800',
  CANCELLADA: 'bg-red-100 text-red-800',
};

// Valid status transitions
const validTransitions: Record<string, string[]> = {
  OBERTA: ['EN_PROCES', 'CANCELLADA'],
  EN_PROCES: ['RESOLTA', 'CANCELLADA'],
  RESOLTA: ['TANCADA', 'EN_PROCES'],
  TANCADA: ['EN_PROCES'],
  CANCELLADA: [],
};

export function SupplierIncidentsTab({ supplierId }: { supplierId: string }) {
  const { user } = useAuth();
  const userFullName = user ? `${user.firstName} ${user.lastName}`.trim() : '';
  const [incidents, setIncidents] = useState<SupplierIncident[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState<SupplierIncident | null>(null);
  const [newStatus, setNewStatus] = useState('');
  const [resolution, setResolution] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: '',
    severity: '',
    detectedAt: new Date().toISOString().split('T')[0],
    assignee: userFullName,
    purchaseOrderId: '',
  });

  const loadIncidents = async () => {
    setLoading(true);
    try {
      const response = await api.get<{ success: boolean; data: SupplierIncident[] }>(
        `/supplier-incidents/${supplierId}`
      );
      setIncidents(response.data || []);
    } catch (err) {
      console.error('[v0] Error loading incidents:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadIncidents();
  }, [supplierId]);

  useEffect(() => {
    if (showForm && userFullName) {
      setFormData(prev => ({
        ...prev,
        assignee: userFullName,
      }));
    }
  }, [showForm, userFullName]);

  const handleSubmit = async () => {
    try {
      const payload: any = {
        supplierId,
        title: formData.title,
        description: formData.description,
        type: formData.type,
        severity: formData.severity,
        detectedAt: formData.detectedAt,
        assignee: formData.assignee,
      };

      // Solo incluye purchaseOrderId si no está vacío
      if (formData.purchaseOrderId && formData.purchaseOrderId.trim()) {
        payload.purchaseOrderId = formData.purchaseOrderId;
      }

      await api.post('/supplier-incidents', payload);
      setShowForm(false);
      setFormData({
        title: '',
        description: '',
        type: '',
        severity: '',
        detectedAt: new Date().toISOString().split('T')[0],
        assignee: userFullName,
        purchaseOrderId: '',
      });
      await loadIncidents();
    } catch (err) {
      console.error('[v0] Error creating incident:', err);
    }
  };

  const handleDelete = async (incidentId: string) => {
    if (!confirm('Estàs segur que vols eliminar aquesta incidència?')) return;
    try {
      await api.delete(`/supplier-incidents/${incidentId}`);
      await loadIncidents();
    } catch (err) {
      console.error('[v0] Error deleting incident:', err);
    }
  };

  const handleChangeStatus = async () => {
    if (!selectedIncident || !newStatus) return;

    try {
      const payload: any = { status: newStatus };
      if ((newStatus === 'RESOLTA' || newStatus === 'TANCADA') && resolution) {
        payload.resolution = resolution;
      }

      await api.patch(`/supplier-incidents/${selectedIncident.id}/status`, payload);
      setShowStatusDialog(false);
      setNewStatus('');
      setResolution('');
      setSelectedIncident(null);
      await loadIncidents();
    } catch (err) {
      console.error('[v0] Error changing status:', err);
    }
  };

  const openStatusDialog = (incident: SupplierIncident) => {
    setSelectedIncident(incident);
    setNewStatus('');
    setResolution('');
    setShowStatusDialog(true);
  };

  const openIncidentCount = incidents.filter(i => ['OBERTA', 'EN_PROCES'].includes(i.status)).length;

  if (loading) {
    return <div className="text-center py-8">Carregant...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Nova Incidència Button */}
      <div className="flex justify-between items-center">
        <div className="flex gap-2">
          {openIncidentCount > 0 && (
            <Badge variant="destructive">{openIncidentCount} oberta{openIncidentCount !== 1 ? 's' : ''}</Badge>
          )}
        </div>
        <Button onClick={() => setShowForm(!showForm)} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Nova incidència
        </Button>
      </div>

      {/* Form */}
      {showForm && (
        <Card className="border-border bg-white">
          <CardHeader>
            <CardTitle>Nova Incidència</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Títol *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Descripció breu del problema"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Descripció detallada</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Detalls de la incidència"
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Tipus *</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Selecciona</option>
                  {Object.entries(incidentTypes).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Gravetat *</label>
                <select
                  value={formData.severity}
                  onChange={(e) => setFormData(prev => ({ ...prev, severity: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Selecciona</option>
                  {Object.entries(severityMap).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Data detecció *</label>
                <input
                  type="date"
                  value={formData.detectedAt}
                  onChange={(e) => setFormData(prev => ({ ...prev, detectedAt: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Responsable</label>
                <input
                  type="text"
                  value={formData.assignee}
                  onChange={(e) => setFormData(prev => ({ ...prev, assignee: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowForm(false)}>Cancel·lar</Button>
              <Button onClick={handleSubmit}>Desa</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      {incidents.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground border rounded-lg">
          <AlertTriangle className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p>Sense incidències registrades</p>
        </div>
      ) : (
        <Card>
          <CardContent className="pt-6">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted">
                    <TableHead>Títol</TableHead>
                    <TableHead>Tipus</TableHead>
                    <TableHead>Gravetat</TableHead>
                    <TableHead>Estat</TableHead>
                    <TableHead>Data detecció</TableHead>
                    <TableHead>Responsable</TableHead>
                    <TableHead className="text-center">Accions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {incidents.map((incident) => (
                    <TableRow key={incident.id} className="hover:bg-muted/50">
                      <TableCell className="text-sm font-medium">{incident.title}</TableCell>
                      <TableCell className="text-sm">{incidentTypes[incident.type] || incident.type}</TableCell>
                      <TableCell>
                        <Badge className={`${severityColors[incident.severity] || 'bg-gray-100'}`}>
                          {severityMap[incident.severity] || incident.severity}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openStatusDialog(incident)}
                          className="flex items-center gap-2"
                        >
                          <Badge className={`${statusColors[incident.status] || 'bg-gray-100'}`}>
                            {statusMap[incident.status] || incident.status}
                          </Badge>
                          <ChevronDown className="h-3 w-3" />
                        </Button>
                      </TableCell>
                      <TableCell className="text-sm">{incident.detectedAt}</TableCell>
                      <TableCell className="text-sm">{incident.assignee || '-'}</TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(incident.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Status Change Dialog */}
      <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Canviar estat de la incidència</DialogTitle>
          </DialogHeader>
          {selectedIncident && (
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium mb-2">Estat actual: <Badge variant="outline">{statusMap[selectedIncident.status]}</Badge></p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Nou estat *</label>
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Selecciona</option>
                  {validTransitions[selectedIncident.status as keyof typeof validTransitions]?.map(status => (
                    <option key={status} value={status}>{statusMap[status]}</option>
                  ))}
                </select>
              </div>

              {(newStatus === 'RESOLTA' || newStatus === 'TANCADA') && (
                <div>
                  <label className="block text-sm font-medium mb-2">Resolució *</label>
                  <textarea
                    value={resolution}
                    onChange={(e) => setResolution(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Descripció de la resolució"
                    rows={3}
                  />
                </div>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={() => setShowStatusDialog(false)}>Cancel·lar</Button>
                <Button onClick={handleChangeStatus} disabled={!newStatus}>Desa</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
